import { AwsUi } from '../../../core/runtime/src';
import { KeyCode } from '../../../core/utils/src/dom';
import { AttributeEditor } from '../../attribute-editor/src';
import { Icon } from '../../icon/src';
import { Spinner } from '../../spinner/src';
import { TagControl } from './tag-control';
import TagEditorController from './controllers/tag-editor-controller';
import FocusHandler from './focus-handler';
// This constant will limit the number of keys or values that can be displayed by the Autosuggest component for performance reasons.
var AUTOSUGGEST_ITEM_LIMIT = 200;
export var TagEditor = AwsUi.__addComponent('awsui-tag-editor', {
    releaseStatus: 'stable',
    properties: {
        // An array of tags that will be displayed to the user. Each tag item has the following properties:
        //
        // * `key` [string]: The key of the tag that will be displayed in the corresponding key field.
        // * `value` [string]: The value of the tag that will be displayed in the corresponding value field.
        // * `existing` [boolean]: Whether this is an existing tag for the resource. If set to `true`, deletion of the tag will set the `markedForRemoval` property to `true`. If set to `false`, deletion of the tag will remove the tag from the `tags` list.
        // * `markedForRemoval` [boolean]: Whether this tag has been marked for removal. This property will be set to `true` by the component when a user tries to remove an existing tag. The item will remain in the `tags` list. When set to `true`, the user will be presented with the option to undo the removal operation.
        // * `valueSuggestionOptions` [Array<AutosuggestProps.Option>]: An array of autosuggest suggestion options associated with the specified tag key.
        tags: {
            type: 'array',
            itemTypes: ['TagEditor.Tag'],
            onChange: function (_oldVal, newVal) {
                this.__controller.tagsPropertyChangeHandler(newVal);
            }
        },
        // Function that is called to retrieve all the keys for a resource.
        // The expected return type of the function should be a promise that resolves to a list of strings of all the keys.
        // e.g. `['key1', 'key2']`
        keysRequest: {
            type: 'function',
            functionType: 'TagEditor.KeysRequestFunction'
        },
        // Function that is called to retrieve all the values for a specified key of a resource.
        // The expected return type of the function should be a promise that resolves to a list of strings of all the values.
        // e.g. `['value1', 'value2']`
        valuesRequest: {
            type: 'function',
            functionType: 'TagEditor.ValuesRequestFunction'
        },
        // Renders the component in a loading state.
        loading: {
            type: 'boolean'
        },
        // The maximum number of tags that a customer can add. Default: 50.
        tagLimit: {
            type: 'integer',
            defaultValue: 50
        },
        // An object containing all the necessary localized strings required by the component.
        i18nStrings: {
            type: 'object',
            objectType: 'TagEditor.I18nStrings'
        },
        // A regular expression string that will be used to override the default acceptable character validation. This should only be
        // used if absolutely necessary.
        allowedCharacterPattern: {
            type: 'string',
            onChange: function (_oldVal, newVal) {
                this.__controller.allowedCharacterPatternPropertyChangeHandler(newVal);
            }
        },
        __errors: {
            type: 'array',
            itemTypes: ['string']
        }
    },
    events: {
        // Fired when any tag operation occurs.
        // The event `detail` object will contain the full updated state of `tags`, as well as if the component is in a `valid` state.
        change: {
            bubbles: true,
            cancelable: false,
            detailType: 'TagEditor.ChangeDetail'
        }
    },
    callbacks: {
        initialized: function () {
            this.__controller = new TagEditorController(this);
            this.__focusHandler = new FocusHandler(this.node);
        }
    },
    functions: {
        // Focuses the first error within the component. If no error is present then no element will be focussed.
        focus: function () {
            var error = this.__controller.getFirstError();
            if (!error) {
                return;
            }
            if (error.key) {
                this.__focusHandler.focusKeyInput(error.index);
            }
            else {
                this.__focusHandler.focusValueInput(error.index);
            }
        }
    },
    render: function () {
        var _this = this;
        // Initial render loop occurs before this property is set
        var i18nStrings = this.i18nStrings || {};
        if (this.loading) {
            return (AwsUi.__createElement("span", { className: "awsui-util-status-inactive" },
                AwsUi.__createElement(Spinner, null),
                " ",
                i18nStrings.loading));
        }
        var markedForRemovalControl = function (index) {
            var undoKeyHandler = function (index) {
                _this.__controller.undoTagRemoval(index);
                _this.__focusHandler.focusRemoveButton(index);
            };
            var keydownHandler = function (event) {
                if (event.keyCode === KeyCode.space) {
                    // Prevent the page from scrolling when toggling the component with the space bar.
                    event.preventDefault();
                }
            };
            return (AwsUi.__createElement("div", { role: "alert", className: "awsui-util-mt-xs" },
                AwsUi.__createElement("span", { class: "awsui-tag-editor-undo-prompt" }, i18nStrings.undoPrompt),
                ' ',
                AwsUi.__createElement("a", { className: "awsui-internal-link-button awsui-tag-editor-undo-button", role: "button", tabindex: "0", onkeyup: function (_a) {
                        var keyCode = _a.keyCode;
                        if (keyCode === KeyCode.enter || keyCode === KeyCode.space) {
                            undoKeyHandler(index);
                        }
                    }, onkeydown: function (e) { return keydownHandler(e); }, onclick: function () { return undoKeyHandler(index); }, formAction: "none" }, i18nStrings.undoButton)));
        };
        var additionalInfo = function () {
            var additionalInfoText = _this.__controller.getAdditionalInfoText();
            var content = _this.__controller.isTagLimitExceeded() ? (AwsUi.__createElement("div", { className: "awsui-form-field-error" },
                AwsUi.__createElement(Icon, { name: "status-warning" }),
                AwsUi.__createElement("span", { className: "awsui-form-field-error__content" }, additionalInfoText))) : (additionalInfoText);
            return AwsUi.__createElement("div", { "aria-live": "polite" }, content);
        };
        return (AwsUi.__createElement(AttributeEditor, { addButtonText: i18nStrings.addButton, removeButtonText: i18nStrings.removeButton, addButtonClick: function () {
                _this.__controller.addTag();
                _this.__focusHandler.focusKeyInput(_this.__controller.tags.length - 1);
            }, removeButtonClick: function (_a) {
                var detail = _a.detail;
                var itemIndex = detail.itemIndex;
                _this.__controller.removeTag(itemIndex);
                if (_this.__controller.tags[itemIndex]) {
                    _this.__focusHandler.focusUndoLink(itemIndex);
                }
            }, disableAddButton: this.__controller.isAddButtonDisabled(), empty: i18nStrings.emptyTags, additionalInfo: additionalInfo(), isItemRemovable: this.__controller.isTagRemovable, definition: [
                {
                    label: function () { return i18nStrings.keyHeader; },
                    control: function (item, index) { return (AwsUi.__createElement(TagControl, { value: item.key, readonly: item.existing, placeholder: i18nStrings.keyPlaceholder, limit: AUTOSUGGEST_ITEM_LIMIT, doRequest: _this.__controller.doKeyRequest.bind(_this.__controller), input: function (_a) {
                            var detail = _a.detail;
                            return _this.__controller.keyInputHandler(detail.value, index);
                        }, delayedInput: _this.__controller.__notifyChange.bind(_this.__controller), blur: function () { return _this.__controller.keyBlurHandler(index); }, loadingText: i18nStrings.keysSuggestionLoading, errorText: i18nStrings.keysSuggestionError, suggestionText: i18nStrings.keySuggestion, tooManySuggestionText: i18nStrings.tooManyKeysSuggestion, options: _this.__controller.__keySuggestions })); },
                    errorText: function (item, index) { return (_this.__errors[index] ? _this.__errors[index].key : null); }
                },
                {
                    label: function () {
                        return (AwsUi.__createElement("span", null,
                            i18nStrings.valueHeader,
                            " - ",
                            AwsUi.__createElement("i", null, i18nStrings.optional)));
                    },
                    control: function (item, index) {
                        if (item.markedForRemoval) {
                            return markedForRemovalControl(index);
                        }
                        return (AwsUi.__createElement(TagControl, { value: item.value, placeholder: i18nStrings.valuePlaceholder, limit: AUTOSUGGEST_ITEM_LIMIT, doRequest: function (value) { return _this.__controller.doValueRequest(item.key, value); }, input: function (_a) {
                                var detail = _a.detail;
                                return _this.__controller.valueInputHandler(detail.value, index);
                            }, delayedInput: _this.__controller.__notifyChange.bind(_this.__controller), loadingText: i18nStrings.valuesSuggestionLoading, errorText: i18nStrings.valuesSuggestionError, suggestionText: i18nStrings.valueSuggestion, tooManySuggestionText: i18nStrings.tooManyValuesSuggestion, options: item.valueSuggestionOptions || [], loadingError: !item.key }));
                    },
                    errorText: function (_item, index) { return (_this.__errors[index] ? _this.__errors[index].value : null); }
                }
            ], items: this.__controller.tags }));
    }
});
//# sourceMappingURL=tag-editor.js.map